import { createSlice } from '@reduxjs/toolkit';
import { RootState } from '../..';
import IAllowedCrewMember, { AllowedCrewMember, deleteAllowedCrewMember as deleteAllowedCrewMemberApi, fetchAllowedCrewMembers } from './allowedCrewMembersAPI';
import { LoadStatus } from '../../../model';
import { createAppThunk } from '../../thunks';

export interface AllowedCrewMembersPanelState {
    items: IAllowedCrewMember[];
    nextPageToken: string | null;
    loadStatus: LoadStatus;
    error: string | null;
}

const initialState: AllowedCrewMembersPanelState = {
    loadStatus: 'idle',
    items: [],
    nextPageToken: null,
    error: null,
};

export const loadAllowedCrewMembers = createAppThunk<AllowedCrewMember, void>(
    'allowedCrewMembers/loadMembers',
    async () => {
        return await fetchAllowedCrewMembers();
    }
);

export const deleteAllowedCrewMember = createAppThunk<string, string>(
    'allowedCrewMembers/deleteAllowedCrewMember',
    async (staffNumber: string) => {
        await deleteAllowedCrewMemberApi(staffNumber);
        return staffNumber;
    }
);

export const allowedCrewMembersSlice = createSlice({
    name: 'allowedCrewMembers',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        resetCrewMembers: (state) => {
            state.items = [];
            state.nextPageToken = null;
            state.loadStatus = 'idle';
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadAllowedCrewMembers.pending, (state) => {
                state.loadStatus = 'loading';
                state.error = null;
            })
            .addCase(loadAllowedCrewMembers.fulfilled, (state, action) => {
                state.loadStatus = 'idle';
                state.items = action.payload.items ?? [];
                state.nextPageToken = !!action.payload.nextPageToken
                    ? JSON.stringify(action.payload.nextPageToken)
                    : null;
                state.error = null;
            })
            .addCase(loadAllowedCrewMembers.rejected, (state, action) => {
                state.loadStatus = 'failed';
                state.error = action.error.message || 'Failed to load crew members';
            })
            .addCase(deleteAllowedCrewMember.pending, (state) => {
                state.loadStatus = 'loading';
                state.error = null;
            })
            .addCase(deleteAllowedCrewMember.fulfilled, (state, action) => {
                state.loadStatus = 'idle';
                state.items = state.items.filter((item) => item.rowKey !== action.payload);
                state.error = null;
            })
            .addCase(deleteAllowedCrewMember.rejected, (state, action) => {
                state.loadStatus = 'failed';
                state.error = action.error.message || 'Failed to delete crew member';
            });
    },
});

export const { clearError, resetCrewMembers } = allowedCrewMembersSlice.actions;

export const selectAllowedCrewMembers = (state: RootState) => state.allowedCrewMembers.items;
export const selectAllowedCrewMembersLoadStatus = (state: RootState) => state.allowedCrewMembers.loadStatus;
export const selectAllowedCrewMembersError = (state: RootState) => state.allowedCrewMembers.error;
export const selectAllowedCrewMembersNextPageToken = (state: RootState) => state.allowedCrewMembers.nextPageToken;

export default allowedCrewMembersSlice.reducer;
